// Tenant registry: single source of truth for who we are and what URLs we
// expose. The same tenant can be reached via multiple hostnames:
//   - canonical: tenant-slug under the platform root
//       e.g. start.tenant-a.morfengund.com
//   - brand alias: the customer-facing brand domain
//       e.g. start.morfengund.com (tenant A), start.orgbx.com (tenant B)
// Both URL forms route to the same SPA + same B2C policy + same adapter —
// only the hostname differs. The dual exposure demonstrates both shapes of
// the multi-tenant abstraction (SaaS-style tenant-slug and white-label
// brand-domain) without code duplication.
//
// Code uses the abstract TenantId everywhere. Only resolveTenant() and
// surfaceUrl() know about hostnames. New tenants are added by extending
// TENANTS, not by touching call sites.
//
// `wireSlug` is the slug the adapter / B2C policies / KV folders use on the
// wire. SPAs map TenantId → wireSlug at API boundaries. User-facing copy
// should render `brand.label` (MoGu / OrX), never the wireSlug.

export type TenantId = "tenant-a" | "tenant-b";
export type Surface = "onboarding" | "online" | "id" | "cdn" | "altersvorsorge";
export type WireSlug = "mogu" | "orx";

export type ThemeMode = "light" | "dark";

export interface BrandPack {
  label: string;
  /** shadcn-style neutral theme: light (MoGu) vs dark (OrX). */
  mode: ThemeMode;
  /** Primary/accent fill colour — also exposed as --hub-brand-accent (avatar). */
  paletteAccent: string;
}

export interface Tenant {
  id: TenantId;
  wireSlug: WireSlug;
  brand: BrandPack;
  // Hostnames this tenant answers on. The FIRST entry is canonical and
  // is used as the default when the current navigation context offers no
  // better hint. Subsequent entries are brand aliases / legacy hosts.
  hostnames: readonly string[];
}

export const TENANTS: Record<TenantId, Tenant> = {
  "tenant-a": {
    id: "tenant-a",
    wireSlug: "mogu",
    brand: { label: "MoGu", mode: "light", paletteAccent: "#171717" },
    hostnames: [
      "tenant-a.morfengund.com", // canonical (tenant-slug under platform root)
      "morfengund.com", // brand alias (customer-facing brand domain)
    ],
  },
  "tenant-b": {
    id: "tenant-b",
    wireSlug: "orx",
    brand: { label: "OrX", mode: "dark", paletteAccent: "#fafafa" },
    hostnames: [
      "tenant-b.morfengund.com", // canonical
      "orgbx.com", // brand alias
    ],
  },
};

// Pre-computed (host → tenant) index, sorted by host length DESC so the
// longest match wins. Without this, `start.tenant-b.morfengund.com` would
// match tenant-A's brand alias `morfengund.com` first (since `.morfengund.com`
// is a suffix of `.tenant-b.morfengund.com`) and route the user onto
// tenant-A's surfaces. The canonical tenant-slug hostname has to be
// considered before any shorter parent alias.
const HOST_INDEX: ReadonlyArray<{ host: string; tenant: TenantId }> = (() => {
  const out: { host: string; tenant: TenantId }[] = [];
  for (const tenant of Object.values(TENANTS)) {
    for (const host of tenant.hostnames) out.push({ host, tenant: tenant.id });
  }
  out.sort((a, b) => b.host.length - a.host.length);
  return out;
})();

function matchHost(hostname: string): { host: string; tenant: TenantId } | null {
  for (const entry of HOST_INDEX) {
    if (hostname === entry.host || hostname.endsWith(`.${entry.host}`)) {
      return entry;
    }
  }
  return null;
}

// Resolve tenant from a hostname. Returns "tenant-a" as a safe default for
// unrecognised hosts (localhost, *.pages.dev preview, direct project URLs)
// so the SPA always renders a usable surface — matches the prior brand.ts
// behaviour where unknown → tenant-a.
export function resolveTenant(
  hostname: string = window.location.hostname,
): TenantId {
  return matchHost(hostname)?.tenant ?? "tenant-a";
}

// Build a surface URL for a tenant. If the current hostname matches one of
// the tenant's hostnames, the result stays on that same root (so a user on
// start.morfengund.com gets back a URL on .morfengund.com, not .tenant-a.).
// Otherwise falls back to the canonical (first) hostname.
//
// Accepts either TenantId (new) or the WireSlug ("mogu"/"orx"). The dual
// accept simplifies the migration: existing call sites holding a wireSlug-
// typed value continue to work without each call site rewriting. The shim
// disappears when the wireSlug type itself is retired.
export function surfaceUrl(
  surface: Surface,
  tenant: TenantId | WireSlug,
  hostname: string = window.location.hostname,
): string {
  const tenantId = isTenantId(tenant) ? tenant : tenantIdFromWireSlug(tenant);
  const t = TENANTS[tenantId];
  const match = matchHost(hostname);
  // Only adopt the current root when it belongs to the requested tenant —
  // otherwise (e.g. cross-tenant surfaceUrl call) fall back to canonical.
  const root = match && match.tenant === tenantId ? match.host : t.hostnames[0];
  return `https://${surface}.${root}`;
}

// Bare B2C custom-domain host for a tenant: `id.<root>`, adopting the current
// root so it's same-site with the surfaces (id.orgbx.com on orgbx.com,
// id.morfengund.com on morfengund.com). Mirrors the adapter's
// BrandConfigResolver.brandDomain (also id.<root>) and is what the signup
// iframe actually loads — so the modal's "Hosted on …" label must use THIS,
// not a hardcoded id.<wireSlug>.morfengund.com (which isn't a real host).
export function b2cHost(
  tenant: TenantId | WireSlug,
  hostname: string = window.location.hostname,
): string {
  return new URL(surfaceUrl("id", tenant, hostname)).host;
}

function isTenantId(v: TenantId | WireSlug): v is TenantId {
  return v === "tenant-a" || v === "tenant-b";
}

function tenantIdFromWireSlug(slug: WireSlug): TenantId {
  for (const t of Object.values(TENANTS)) {
    if (t.wireSlug === slug) return t.id;
  }
  return "tenant-a";
}

// Customer-facing brand label for a tenant or wireSlug. Use this in UI copy
// instead of rendering the raw wireSlug — e.g. brandLabel("mogu") === "MoGu".
export function brandLabel(tenant: TenantId | WireSlug): string {
  const tenantId = isTenantId(tenant) ? tenant : tenantIdFromWireSlug(tenant);
  return TENANTS[tenantId].brand.label;
}

// Neutral shadcn-style palettes mapped onto the existing --hub-* token names.
// The differentiator between brands is ONLY light (MoGu) vs dark (OrX) — no
// brand accent hue. Keys mirror the --hub-* vars the inline stylesheets use.
const HUB_THEMES: Record<ThemeMode, Record<string, string>> = {
  light: {
    "--hub-bg": "#ffffff",
    "--hub-surface": "#f5f5f5",
    "--hub-card": "#ffffff",
    "--hub-ink": "#0a0a0a",
    "--hub-ink-soft": "#737373",
    "--hub-muted": "#737373",
    "--hub-line": "#e5e5e5",
    "--hub-accent": "#171717",
    "--hub-accent-600": "#000000",
    "--hub-on-accent": "#fafafa",
    "--hub-ring": "#a3a3a3",
    "--hub-danger": "#dc2626",
    "--hub-shadow": "0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)",
  },
  dark: {
    "--hub-bg": "#0a0a0a",
    "--hub-surface": "#171717",
    "--hub-card": "#111111",
    "--hub-ink": "#fafafa",
    "--hub-ink-soft": "#a1a1a1",
    "--hub-muted": "#a1a1a1",
    "--hub-line": "#262626",
    "--hub-accent": "#fafafa",
    "--hub-accent-600": "#ffffff",
    "--hub-on-accent": "#0a0a0a",
    "--hub-ring": "#525252",
    "--hub-danger": "#f87171",
    "--hub-shadow": "0 1px 2px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.5)",
  },
};

// Apply the resolved tenant's neutral theme as GLOBAL CSS variables so the whole
// surface (buttons, links, cards, avatar) follows the shadcn-style black/white
// system — MoGu light, OrX dark. Call once at startup; overrides the static
// --hub-* base theme defined inline by the page stylesheet.
export function applyBrandTheme(hostname?: string): void {
  const brand = TENANTS[resolveTenant(hostname)].brand;
  const s = document.documentElement.style;
  for (const [name, value] of Object.entries(HUB_THEMES[brand.mode])) {
    s.setProperty(name, value);
  }
  // Avatar tint references --hub-brand-accent (falls back to --hub-accent).
  s.setProperty("--hub-brand-accent", brand.paletteAccent);
}

// ─── Backwards-compatibility shims ───
// Existing call sites use Brand / resolveBrand. They keep working through
// the wireSlug translation. Removed in a follow-up commit once all call
// sites import the tenant API directly.
export type Brand = WireSlug;
export function resolveBrand(hostname?: string): Brand {
  return TENANTS[resolveTenant(hostname)].wireSlug;
}
